import { useCallback, useEffect, useRef, useState } from 'react';
import useUpdateStepAPI from './useUpdateStepAPI.ts';
import useCustomDebouncedCallback from '@/hooks/useCustomDebouncedCallback.ts';
import { AutomationStep } from '@/types/automationSteps.ts';

// syncWithAPI: sometimes the backend will make changes to the step fields that we want to sync with the frontend
// (specifically for integration fields)

type UseUpdateStepProps<TFields> = {
  step: TFields;
  syncWithAPI?: boolean;
};

export function useUpdateStepFields<TFields extends Partial<AutomationStep>>({
  step,
  syncWithAPI = false,
}: UseUpdateStepProps<TFields>) {
  const [fields, setFields] = useState<TFields>(step);
  const { mutate: updateStep, isPending } = useUpdateStepAPI();
  const pendingFieldsRef = useRef<Partial<TFields>>({});
  const [hasScheduledEdit, setHasScheduledEdit] = useState(false);

  useEffect(() => {
    if (syncWithAPI && !isPending && !hasScheduledEdit) {
      setFields(step);
    }
  }, [step, syncWithAPI, isPending, hasScheduledEdit]);

  const flushPendingFields = () => {
    const updatedFields = pendingFieldsRef.current;
    pendingFieldsRef.current = {};
    setHasScheduledEdit(false);
    if (Object.keys(updatedFields).length > 0) {
      updateStep({ step_id: step.step_id!, step_data: { ...updatedFields } });
    }
  };

  const debouncedUpdateStep = useCustomDebouncedCallback(flushPendingFields, []);

  const noDelayUpdateStep = useCustomDebouncedCallback(
    flushPendingFields,
    [],
    { delay: 0 },
  );

  const onChange = useCallback(
    (updatedFields: Partial<TFields>) => {
      setFields((prev) => ({ ...prev, ...updatedFields }));
      pendingFieldsRef.current = { ...pendingFieldsRef.current, ...updatedFields };
      setHasScheduledEdit(true);
      debouncedUpdateStep();
    },
    [debouncedUpdateStep],
  );

  const onChangeNoDelay = useCallback(
    (updatedFields: Partial<TFields>) => {
      setFields((prev) => ({ ...prev, ...updatedFields }));
      pendingFieldsRef.current = { ...pendingFieldsRef.current, ...updatedFields };
      setHasScheduledEdit(true);
      noDelayUpdateStep();
    },
    [noDelayUpdateStep],
  );

  return { fields, onChange, onChangeNoDelay };
}
